import * as d3 from "d3";
import { Selection, BaseType } from "d3";
import * as moment from "moment";
import "moment-timezone";
import * as FontFaceObserver from "fontfaceobserver";
import {
    CalendarState,
    OwnerPage,
    MonthDivider,
    PrintPdfExtraPageResponse,
    CalendarDrawerSettings,
    CalendarDrawerContent,
} from "burde";
import {
    CalendarDimensions,
    MonthDividerNumberOfPages,
    CalendarType,
    NotebookDefaultMaxNumberOfExtraPages,
    NotebookMaxNumberOfExtraPagesForB5,
    LegacyFonts,
} from "svg/constants";
import { drawErrorPage } from "svg/draw/draw-error-page";
import { drawSinglePage } from "svg/draw/draw-single-page";
import { drawDualPage } from "svg/draw/draw-dual-page";
import { drawSinglePageCover } from "svg/draw/draw-single-page-cover";
import { drawOwnerPage } from "svg/draw/draw-owner-page";
import { drawSinglePageBackside } from "svg/draw/draw-single-page-backside";
import { drawSalesPage } from "svg/draw/draw-sales-page";
import { drawDynamicYearOverview } from "./dynamic-layout/draw-dynamic-year-overview";
import {
    DayModuleElement,
    DefaultSize,
    LayoutFont,
    MomentLocales,
    MomentTimeZones,
    SvgCalendarLayout,
    WeekModuleElement,
} from "ui/constants";
import { drawInformationPage } from "./draw-information-page";
import { drawExtraPage } from "./draw-extra-page";
import { drawMonthDivider, isStartOfMonth } from "./draw-month-divider";
import { BleedSize } from "svg/constants";
import { applyFontData } from "./helpers/preview-helper";
import { getCalendarMaxNumberOfExtraPages } from "page/calendar/helper";

export type D3Selection = Selection<BaseType, {}, null, undefined>;

const drawCoverContent = (
    base: D3Selection,
    calendar: CalendarState,
    calendarId: number | null,
    ownerPageImageUrl: string | null,
    Header_WeekNumberPrefix: string,
    ownerPage: OwnerPage | null,
    calendarDrawerContent: CalendarDrawerContent,
    drawCoverOnly: boolean,
) => {
    const calendarSize =
        calendar.calendarSize != "" ? calendar.calendarSize : "A5";
    const calendarDimensions = CalendarDimensions.find(
        (x) => x.calendarSize === calendarSize,
    );

    if (calendarDimensions == undefined) return;

    const yearOverviewStartDate = moment(calendar.startDate, "YYYY-MM-DD");

    const customOrderId =
        calendarDrawerContent !== null &&
        calendarDrawerContent.customerInformation !== null
            ? calendarDrawerContent.customerInformation.customOrderId
            : null;

    if (!drawCoverOnly) {
        drawDynamicYearOverview(
            base,
            false,
            yearOverviewStartDate.clone().add(1, "year"),
            calendarDrawerContent.metadata,
            calendar.culture,
            calendarDimensions,
            calendarId,
            Header_WeekNumberPrefix,
            false,
            customOrderId,
        );
    }

    // Page 4 cover (back)
    drawSinglePageBackside(
        base,
        calendar.calendarSize,
        calendarDimensions,
        calendarDrawerContent.backCoverImage,
        calendar.backCoverColor,
        calendarDrawerContent.customBackImage,
    );
    // Page 1 cover (front)
    drawSinglePageCover(
        base,
        calendar.calendarSize,
        calendarDimensions,
        calendarDrawerContent.coverImage,
        calendar.coverColor,
        calendarDrawerContent.customCoverImage,
        calendar.coverTexts,
        calendarDrawerContent.fonts,
    );

    if (!drawCoverOnly) {
        // First year overview (same year as the start date)
        drawDynamicYearOverview(
            base,
            false,
            yearOverviewStartDate,
            calendarDrawerContent.metadata,
            calendar.culture,
            calendarDimensions,
            calendarId,
            Header_WeekNumberPrefix,
            true,
            customOrderId,
        );

        drawOwnerPage(
            base,
            calendar,
            calendarId,
            ownerPageImageUrl,
            ownerPage,
            customOrderId,
            false,
        );
    }
};

const drawNotebookContent = (
    base: D3Selection,
    calendar: CalendarState,
    calendarDrawerSettings: CalendarDrawerSettings,
    atDate: moment.Moment,
    pageNumber: number,
    drawWeekModulePlaceHolders: boolean,
    Header_WeekNumberPrefix: string,
    fontData: LayoutFont | null,
    calendarLayout: SvgCalendarLayout | null,
    dayModuleElements: DayModuleElement[] | null,
    weekModuleElements: WeekModuleElement[] | null,
    extraPageResponse: PrintPdfExtraPageResponse | null,
    calendarDrawerContent: CalendarDrawerContent,
) => {
    const calendarSize =
        calendar.calendarSize != "" ? calendar.calendarSize : "A5";
    const calendarDimensions = CalendarDimensions.find(
        (x) => x.calendarSize === calendarSize,
    );

    if (calendarDimensions == undefined || !calendarLayout) return;

    const NotebookMaxNumberOfExtraPages =
        calendar.calendarSize.toUpperCase() === "B5"
            ? NotebookMaxNumberOfExtraPagesForB5
            : NotebookDefaultMaxNumberOfExtraPages;

    const pagesToDraw = !calendarDrawerSettings.isConfiguratorView
        ? NotebookMaxNumberOfExtraPages - calendar.customNumberOfEndPages
        : 2;

    for (let i = 0; i < pagesToDraw; i += 2) {
        if (!calendarDrawerSettings.isConfiguratorView) {
            const containerId = `svgContainer${pageNumber}`;

            const container = base
                .append("div")
                .attr("id", containerId)
                .attr("class", "svg-container");
            // Don't be fooled by the name - drawSinglePage still draws two pages.
            drawSinglePage(
                container,
                calendar,
                calendarDrawerSettings,
                pageNumber,
                calendarLayout,
                atDate,
                Header_WeekNumberPrefix,
                fontData,
                calendarDimensions,
                dayModuleElements,
                weekModuleElements,
                calendarDrawerContent,
            );
        } else {
            // Dual pages are only for the configurator (aka not the printPdf route).
            drawDualPage(
                base,
                calendar,
                calendarDrawerSettings,
                pageNumber,
                calendarLayout,
                atDate,
                drawWeekModulePlaceHolders,
                Header_WeekNumberPrefix,
                fontData,
                dayModuleElements,
                weekModuleElements,
                calendarDrawerContent,
            );
        }
    }

    let numberOfPagesLeft = NotebookMaxNumberOfExtraPages - pagesToDraw;

    if (
        !calendarDrawerSettings.isConfiguratorView &&
        !calendarDrawerSettings.drawCoverOnly
    ) {
        // For the rest of the notebook, draw the extra pages.
        if (calendar.extraPages.length > 0 && extraPageResponse !== null) {
            for (let i = 0; i < calendar.extraPages.length; i++) {
                if (numberOfPagesLeft <= 0) {
                    // Break if there are somehow more end pages
                    // than available pages left in the notebook.
                    break;
                }

                const extraPage = extraPageResponse.extraPages.find(
                    (x) => x.id === calendar.extraPages[i],
                );

                if (extraPage !== undefined) {
                    drawExtraPage(
                        base,
                        extraPage,
                        pageNumber,
                        calendarDimensions,
                        calendar,
                        Header_WeekNumberPrefix,
                        calendarDrawerContent,
                    );
                    pageNumber += 2;
                }

                numberOfPagesLeft -= 2;
            }
        }
        // Draw generic extra pages to fill out the rest of the notebook
        if (extraPageResponse !== null && numberOfPagesLeft > 0) {
            const defaultExtraPage = extraPageResponse.extraPages.find(
                (x) => x.id === extraPageResponse.default,
            );

            if (defaultExtraPage !== undefined) {
                for (let i = 0; i < numberOfPagesLeft; i += 2) {
                    drawExtraPage(
                        base,
                        defaultExtraPage,
                        pageNumber,
                        calendarDimensions,
                        calendar,
                        Header_WeekNumberPrefix,
                        calendarDrawerContent,
                    );
                    pageNumber += 2;
                }
            }
        }

        // Drawing the last empty page to the calendar.
        const container = base
            .append("div")
            .attr("id", "blank-page")
            .attr("class", "svg-container");
        const blankPage = container
            .append("svg")
            .attr("width", "100%")
            .attr("height", "100%")
            .attr("preserveAspectRatio", "xMinYMin slice")
            .attr(
                "viewBox",
                `0 0 ${calendarDimensions.svgWidth} ${calendarDimensions.svgHeight}`,
            )
            .classed("svg-content", true);

        const background = blankPage
            .append("g")
            .classed("background-content", true);

        background
            .append("svg:image")
            .attr("x", 0)
            .attr("y", 0)
            .attr("width", calendarDimensions.svgWidth)
            .attr("height", calendarDimensions.svgHeight)
            .attr(
                "xlink:href",
                `images/allWhite.svg?width=${calendarDimensions.svgWidth}&height=${calendarDimensions.svgHeight}`,
            )
            .attr("preserveAspectRatio", "xMidYMid slice");

        background
            .append("g")
            .attr("transform", `translate(${BleedSize}, ${BleedSize})`)
            .classed("main-content", true);
    }
};

const drawCalendarContent = async (
    base: D3Selection,
    calendar: CalendarState,
    calendarDrawerSettings: CalendarDrawerSettings,
    atDate: moment.Moment,
    endDate: moment.Moment,
    pageNumber: number,
    drawWeekModulePlaceHolders: boolean,
    Header_WeekNumberPrefix: string,
    fontData: LayoutFont | null,
    calendarLayout: SvgCalendarLayout | null,
    dayModuleElements: DayModuleElement[] | null,
    weekModuleElements: WeekModuleElement[] | null,
    extraPageResponse: PrintPdfExtraPageResponse | null,
    monthDivider: MonthDivider | null,
    calendarDrawerContent: CalendarDrawerContent,
) => {
    const calendarSize =
        calendar.calendarSize !== "" ? calendar.calendarSize : DefaultSize;
    const calendarDimensions = CalendarDimensions.find(
        (x) => x.calendarSize === calendarSize,
    );

    if (calendarDimensions == undefined || !calendarLayout) return;

    let salesPageDate = null;

    if (
        !calendarDrawerSettings.drawCoverOnly &&
        !calendarDrawerSettings.isConfiguratorView &&
        calendarDrawerContent.salesPages
    ) {
        salesPageDate = atDate
            .clone()
            .add(calendarDrawerContent.salesPages.placedInWeek, "week");
    }

    while (atDate < endDate) {
        if (
            calendar.monthDivider !== null &&
            monthDivider !== null &&
            isStartOfMonth(atDate, endDate)
        ) {
            drawMonthDivider(
                base,
                monthDivider,
                pageNumber,
                calendarDimensions,
                calendar,
                calendarDrawerSettings,
                Header_WeekNumberPrefix,
                atDate,
                calendarDrawerContent,
            );
            pageNumber += 2;
        }

        if (
            salesPageDate &&
            atDate.isSame(salesPageDate) &&
            calendarDrawerContent.salesPages
        ) {
            // If this is an even page number, add an additional week to make sure the sales pages gets a sheet of its own.
            if (pageNumber % 2 < 1) {
                salesPageDate.add(1, "week");
            } else {
                const container = base
                    .append("div")
                    .attr("id", "sales")
                    .attr("class", "svg-container");

                drawSalesPage(
                    container,
                    calendarDrawerContent.salesPages,
                    calendarDimensions,
                );
                pageNumber += 2;
            }
        }

        if (!calendarDrawerSettings.isConfiguratorView) {
            const containerId = `svgContainer${pageNumber}`;

            const container = base
                .append("div")
                .attr("id", containerId)
                .attr("class", "svg-container");

            drawSinglePage(
                container,
                calendar,
                calendarDrawerSettings,
                pageNumber,
                calendarLayout,
                atDate,
                Header_WeekNumberPrefix,
                fontData,
                calendarDimensions,
                dayModuleElements,
                weekModuleElements,
                calendarDrawerContent,
            );
        } else {
            // Dual pages are only for the configurator (aka not the printPdf route).
            drawDualPage(
                base,
                calendar,
                calendarDrawerSettings,
                pageNumber,
                calendarLayout,
                atDate,
                drawWeekModulePlaceHolders,
                Header_WeekNumberPrefix,
                fontData,
                dayModuleElements,
                weekModuleElements,
                calendarDrawerContent,
            );
        }

        pageNumber++;

        const leftLayout = calendarLayout.endpoints.leftLayout;
        const rightLayout = calendarLayout.endpoints.rightLayout;
        const leftNumberOfDays = leftLayout ? leftLayout.numberOfDays : 0;
        const rightNumberOfDays = rightLayout ? rightLayout.numberOfDays : 0;

        if (leftNumberOfDays == 0 && rightNumberOfDays == 0) {
            break;
        }

        const numberOfDays = leftNumberOfDays + rightNumberOfDays;

        atDate = atDate.clone().add(numberOfDays, "days");
    }

    if (
        !calendarDrawerSettings.isConfiguratorView &&
        !calendarDrawerSettings.drawCoverOnly
    ) {
        if (calendar.extraPages.length > 0 && extraPageResponse !== null) {
            for (let i = 0; i < calendar.extraPages.length; i++) {
                const extraPage = extraPageResponse.extraPages.find(
                    (x) => x.id === calendar.extraPages[i],
                );

                if (extraPage !== undefined) {
                    drawExtraPage(
                        base,
                        extraPage,
                        pageNumber,
                        calendarDimensions,
                        calendar,
                        Header_WeekNumberPrefix,
                        calendarDrawerContent,
                    );
                    pageNumber += 2;
                }
            }
        }

        let numberOfPagesLeft = getCalendarMaxNumberOfExtraPages(
            calendar.calendarType,
            calendar.calendarSize,
        );

        if (calendar.monthDivider !== null) {
            numberOfPagesLeft -= MonthDividerNumberOfPages;
        }

        numberOfPagesLeft -= calendar.extraPages.length * 2;

        if (extraPageResponse !== null) {
            const defaultExtraPage = extraPageResponse.extraPages.find(
                (x) => x.id === extraPageResponse.default,
            );

            if (defaultExtraPage !== undefined) {
                for (let i = 0; i < numberOfPagesLeft; i += 2) {
                    drawExtraPage(
                        base,
                        defaultExtraPage,
                        pageNumber,
                        calendarDimensions,
                        calendar,
                        Header_WeekNumberPrefix,
                        calendarDrawerContent,
                    );
                    pageNumber += 2;
                }
            }
        }

        // Drawing the last empty page to the calendar.
        const container = base
            .append("div")
            .attr("id", "blank-page")
            .attr("class", "svg-container");
        const blankPage = container
            .append("svg")
            .attr("width", "100%")
            .attr("height", "100%")
            .attr("preserveAspectRatio", "xMinYMin slice")
            .attr(
                "viewBox",
                `0 0 ${calendarDimensions.svgWidth} ${calendarDimensions.svgHeight}`,
            )
            .classed("svg-content", true);

        const background = blankPage
            .append("g")
            .classed("background-content", true);

        background
            .append("svg:image")
            .attr("x", 0)
            .attr("y", 0)
            .attr("width", calendarDimensions.svgWidth)
            .attr("height", calendarDimensions.svgHeight)
            .attr(
                "xlink:href",
                `images/allWhite.svg?width=${calendarDimensions.svgWidth}&height=${calendarDimensions.svgHeight}`,
            )
            .attr("preserveAspectRatio", "xMidYMid slice");

        background
            .append("g")
            .attr("transform", `translate(${BleedSize}, ${BleedSize})`)
            .classed("main-content", true);
    }
};

const drawCalendar = async (
    calendar: CalendarState,
    calendarDrawerSettings: CalendarDrawerSettings,
    calendarDrawerContent: CalendarDrawerContent,
    ref: HTMLDivElement | string = "body",
) => {
    if (calendar.culture !== null) {
        const momentLocale = MomentLocales[calendar.culture];
        const momentTimeZone = MomentTimeZones[calendar.culture];

        moment.locale(momentLocale);
        moment.tz.setDefault(momentTimeZone);
    }

    const base = d3.select<BaseType, object>(ref as BaseType);
    base.html("");
    let layoutFontData: LayoutFont | null = null;
    const fontObservers: Array<FontFaceObserver> = [];
    const fontStyles: Array<number> =
        calendar.fontId && !calendar.fontSetName ? [calendar.fontId] : [];
    const legacyFontStyles: Array<string> = calendar.fontSetName
        ? [calendar.fontSetName]
        : [];

    if (calendar.coverTexts && calendar.coverTexts.length > 0) {
        for (const text of calendar.coverTexts) {
            if (
                text.fontSetName &&
                !legacyFontStyles.includes(text.fontSetName)
            ) {
                legacyFontStyles.push(text.fontSetName);
            } else if (text.fontId && !fontStyles.includes(text.fontId)) {
                fontStyles.push(text.fontId);
            }
        }
    }

    fontStyles.forEach((fontId) => {
        const fontData = calendarDrawerContent.fonts
            ? calendarDrawerContent.fonts.fontResources.find(
                  (x) => x.id === fontId,
              )
            : undefined;

        if (fontData !== undefined) {
            applyFontData(fontData, base);
            fontObservers.push(new FontFaceObserver(fontData["font-family"]));

            if (fontId === calendar.fontId && !calendar.fontSetName) {
                layoutFontData = fontData;
                base.style("font-family", layoutFontData["font-family"]);
            }
        }
    });
    legacyFontStyles.forEach((fontSetName) => {
        const fontData = LegacyFonts.find((x) => x.title === fontSetName);

        if (fontData !== undefined) {
            applyFontData(fontData, base);
            fontObservers.push(new FontFaceObserver(fontData["font-family"]));

            if (fontData.title === calendar.fontSetName) {
                layoutFontData = fontData;
                base.style("font-family", layoutFontData["font-family"]);
            }
        }
    });

    const draw = async () => {
        const calendarSize =
            calendar.calendarSize && calendar.calendarSize !== ""
                ? calendar.calendarSize
                : "A5";
        const calendarDimensions = CalendarDimensions.find(
            (x) => x.calendarSize === calendarSize,
        );

        if (calendarDimensions == undefined) return;

        // The calendar should always start on a monday no matter the start date (previously
        // called "calendarStartDate")
        const atDate = moment(calendar.startDate, "YYYY-MM-DD")
            .startOf("month")
            .startOf("isoWeek");
        const endDate = atDate
            .clone()
            .add(calendarDrawerSettings.numberOfWeeks, "weeks");

        let pageNumber = 1;

        if (calendarDrawerContent.layout === null) {
            drawErrorPage(base, calendarDimensions);
            return;
        }

        const drawCover =
            !calendarDrawerSettings.isConfiguratorView &&
            !calendarDrawerSettings.drawInsertOnly;

        if (drawCover) {
            drawCoverContent(
                base,
                calendar,
                calendarDrawerContent.calendarId,
                calendarDrawerContent.ownerPageImageUrl,
                calendarDrawerContent.weekNumberPrefix,
                calendarDrawerContent.ownerPage,
                calendarDrawerContent,
                calendarDrawerSettings.drawCoverOnly,
            );
        } else {
            const customOrderId =
                calendarDrawerContent !== null &&
                calendarDrawerContent.customerInformation !== null
                    ? calendarDrawerContent.customerInformation.customOrderId
                    : null;

            drawOwnerPage(
                base,
                calendar,
                calendarDrawerContent.calendarId,
                calendarDrawerContent.ownerPageImageUrl,
                calendarDrawerContent.ownerPage,
                customOrderId,
                false,
            );
        }

        if (!calendarDrawerSettings.drawCoverOnly) {
            if (
                calendar.calendarType.toLowerCase() ===
                CalendarType.Calendar.toLowerCase()
            ) {
                drawCalendarContent(
                    base,
                    calendar,
                    calendarDrawerSettings,
                    atDate,
                    endDate,
                    pageNumber,
                    calendarDrawerSettings.drawWeekModulePlaceholders,
                    calendarDrawerContent.weekNumberPrefix,
                    layoutFontData,
                    calendarDrawerContent.layout,
                    calendarDrawerContent.dayModules,
                    calendarDrawerContent.weekModules,
                    calendarDrawerContent.extraPages,
                    calendarDrawerContent.monthDivider,
                    calendarDrawerContent,
                );
            } else if (
                calendar.calendarType.toLowerCase() ===
                CalendarType.Notebook.toLowerCase()
            ) {
                drawNotebookContent(
                    base,
                    calendar,
                    calendarDrawerSettings,
                    atDate,
                    pageNumber,
                    calendarDrawerSettings.drawWeekModulePlaceholders,
                    calendarDrawerContent.weekNumberPrefix,
                    layoutFontData,
                    calendarDrawerContent.layout,
                    calendarDrawerContent.dayModules,
                    calendarDrawerContent.weekModules,
                    calendarDrawerContent.extraPages,
                    calendarDrawerContent,
                );
            }
        }

        if (
            !calendarDrawerSettings.isConfiguratorView &&
            !calendarDrawerSettings.drawCoverOnly
        ) {
            // EAN and other information
            drawInformationPage(
                base,
                calendar,
                calendarDrawerContent.customerInformation,
                calendarDimensions,
                calendarDrawerContent.calendarId,
                calendarDrawerContent.spiral,
                calendarDrawerContent.rubberband,
                calendarDrawerContent.article,
            );
        }
    };

    const loadImage = (url: string) => {
        return new Promise<void>((resolve) => {
            const img = new Image();
            img.onload = () => {
                //@ts-ignore
                resolve();
            };
            img.src = url;
        });
    };

    const images: any = [];
    // Fonts need to be loaded before we draw, if not there is a risk that texts
    // will be drawn out of bounds
    Promise.all(fontObservers).then(() => {
        if (
            calendarDrawerSettings.isConfiguratorView &&
            calendarDrawerSettings.drawCoverOnly
        ) {
            if (!!calendarDrawerContent.customCoverImage) {
                images.push(
                    loadImage(calendarDrawerContent.customCoverImage.url),
                );
            }

            if (
                !!calendarDrawerContent.coverImage &&
                !!calendarDrawerContent.coverImage.endpoints
            ) {
                images.push(
                    loadImage(
                        calendarDrawerContent.coverImage.endpoints.preview,
                    ),
                );
            }

            if (!!calendarDrawerContent.backCoverImage) {
                images.push(
                    loadImage(
                        calendarDrawerContent.backCoverImage.endpoints.preview,
                    ),
                );
            }
        }

        if (images.length > 0) {
            Promise.all(images).then(() => {
                draw();
            });
        } else {
            draw();
        }
    });
};

export { drawCalendar };
